import { Song, SongPreview } from '~/interfaces';

/** What makes one song like another, most telling first. */
const WEIGHTS = { artist: 40, genre: 15, language: 10, era: 5 };

/** Songs within this many years of each other count as being from the same era. */
const ERA_YEARS = 5;

export interface SimilarSong {
  song: SongPreview;
  score: number;
  /** The strongest thing the two have in common, for a one-word explanation. */
  reason: 'same artist' | 'same genre' | 'same language' | 'same era';
}

const normalise = (value: string) => value.trim().toLowerCase();

const sameArtist = (a: Pick<Song, 'artist'>, b: SongPreview) => normalise(a.artist) === normalise(b.artist);

const sameGenre = (a: Pick<Song, 'genre'>, b: SongPreview) =>
  !!a.genre && !!b.genre && normalise(a.genre) === normalise(b.genre);

const sharedLanguage = (a: Pick<Song, 'language'>, b: SongPreview) => {
  const languages = new Set((Array.isArray(a.language) ? a.language : [a.language]).map(normalise));

  return b.language.some((language) => languages.has(normalise(language)));
};

const sameEra = (a: Pick<Song, 'year'>, b: SongPreview) => {
  const from = Number(a.year);
  const to = Number(b.year);

  return Number.isFinite(from) && Number.isFinite(to) && Math.abs(from - to) <= ERA_YEARS;
};

/**
 * Songs like the one just sung. This asks "more of that", which is a different question from the
 * personal recommendations on the song list - those ask "what suits this singer" and are built
 * from their history rather than from one song.
 */
export const findSimilarSongs = (song: Song, candidates: SongPreview[], limit = 3): SimilarSong[] => {
  const results: SimilarSong[] = [];

  for (const candidate of candidates) {
    if (candidate.id === song.id) continue;

    let score = 0;
    let reason: SimilarSong['reason'] | null = null;

    if (sameArtist(song, candidate)) {
      score += WEIGHTS.artist;
      reason ??= 'same artist';
    }
    if (sameGenre(song, candidate)) {
      score += WEIGHTS.genre;
      reason ??= 'same genre';
    }
    if (sharedLanguage(song, candidate)) {
      score += WEIGHTS.language;
      reason ??= 'same language';
    }
    if (sameEra(song, candidate)) {
      score += WEIGHTS.era;
      reason ??= 'same era';
    }

    // Sharing only an era is not a resemblance worth offering - half the catalogue would qualify.
    if (reason === null || score <= WEIGHTS.era) continue;

    results.push({ song: candidate, score, reason });
  }

  return results.sort((a, b) => b.score - a.score || a.song.artist.localeCompare(b.song.artist)).slice(0, limit);
};
